import { IFinishedIdentifications } from 'components/cattles/digitizationPage/ui/table-header/hook/useFinishedIdentifications'
import { DailyShiftProps } from 'hook/query/useGetDailyShifts'

import {
	IMilkingTableRequest,
	IMilkingTableResponse,
} from 'components/milk-yield/components/MilkYeild-Table/components/IMilkingTableResponse'
import {
	EventsEnum,
	ICheckupList,
	IFatness,
	IFiltersLamenessTable,
	InspectionStatus,
	LactationStatus,
	LamenessStatus,
	TreatmentStatus,
} from 'models'
import {
	IAvailableCowTags,
	IAvailableFilterValuesResponse,
	ICowGraphResponse,
	ICowLightInfo,
	ICowsList,
	ICowsListResponse,
	IFatnessGraphResponse,
	IFatnessHealthTable,
	IHoofHealthTable,
	IIdentificationProps,
	IIdentificationResponse,
	ILamenessGraphResponse,
	IPatchChangeTagIdProps,
	IUpdateLamenessScoreRequest,
	IUpdateLamenessScoreResponse,
	ITreatmentSchema,
	ITreatmentSchemaOptions,
	ITreatmentSchemaOptionsResponse,
	ITrims,
	ITrimsBatching,
	patchCowInfoType,
} from 'models/ICattlesData'
import {
	IMilkYieldChartRequest,
	IMilkYieldChartResponse,
} from 'models/IMilkYieldChartTypes'
import axAPI from '../httpConfig'

const LAMENESS_EXAMINATION_ORDERING = 'lameness_examination_priority'

export default class CattlesApiService {
	static getDailyStatistics = async (groupId: string | number) => {
		return await axAPI.get(
			`/cows/statistics${groupId !== 'all' ? '?group_id=' + groupId : ''}`,
		)
	}
	static getInfoFromDigitizatedFrame = async (cameraId: number) => {
		return axAPI.get(`/cows/camera/${cameraId}/milking`)
	}
	static patchCowTagInfo = async (obj: patchCowInfoType) => {
		return await axAPI({
			method: 'PATCH',
			url: `/cows/${obj.cow_id}`,
			data: obj,
		})
	}
	static updateLamenessScore = async (
		data: IUpdateLamenessScoreRequest,
	): Promise<IUpdateLamenessScoreResponse> => {
		const response = await axAPI.patch('/cows/limpness', data)
		return response.data
	}
	static patchForceCowTagNumber = async (tagId: number, cowId: number) => {
		return await axAPI({
			method: 'PATCH',
			url: `/cows/${cowId}/force`,
			data: { tag_id: tagId },
		})
	}
	static buildQueryParams = (
		groupId?: number,
		fatnessPoint?: number[],
		tags?: number[],
		groups?: number[],
		fatness?: IFatness,
		examiners?: { ids: number[] },
		diagnoses?: { ids: number[] },
		lameness?: {
			statuses: LamenessStatus[]
			startOf?: number | string
			endOf?: number | string
		},
		treatmentStatus?: {
			statuses: TreatmentStatus[]
			startOf?: number | string
			endOf?: number | string
		},
		inspectionStatus?: {
			statuses: InspectionStatus[]
			startOf?: number
			endOf?: number
		},
		cowAge?: {
			startOf?: number | string
			endOf?: number | string
		},
		lactation?: {
			statuses: LactationStatus[]
			startOf?: number | string
			endOf?: number | string
		},
		lastTrim?: {
			startOf?: number | string
			endOf?: number | string
		},
		lastMilking?: {
			startOf?: number | string
			endOf?: number | string
		},
		lastLamenessMeasurement?: {
			startOf?: number | string
			endOf?: number | string
		},
		responsiblePersons?: { ids: number[] },
		isLateTrims?: boolean,
		dateRange?: {
			startDate: number
			endDate: number
		},
		page?: number,
		pageSize?: number,
		tag_id_search?: string,
		identified?: boolean,
		ordering?: string | null,
		columns?: string[],
	): string => {
		const queryParams = new URLSearchParams()

		if (groupId) queryParams.append('group_id', groupId.toString())
		if (fatnessPoint && fatnessPoint.length > 0) {
			queryParams.append('fatness', fatnessPoint.join(','))
		}
		if (tags && tags.length > 0) {
			queryParams.append('tags', tags.join(','))
		}
		lameness &&
			lameness?.startOf !== '' &&
			queryParams.append('limpness_days_start', lameness.startOf.toString())
		lameness &&
			lameness?.endOf !== '' &&
			queryParams.append('limpness_days_end', lameness.endOf.toString())
		if (lameness?.statuses?.length > 0) {
			queryParams.append('limpness', lameness.statuses.join(','))
		}
		treatmentStatus &&
			treatmentStatus?.statuses?.every(
				status => status === TreatmentStatus.Treated,
			) &&
			treatmentStatus.startOf &&
			treatmentStatus.startOf != null &&
			queryParams.append(
				'treatment_days_start',
				treatmentStatus.startOf.toString(),
			)
		treatmentStatus &&
			treatmentStatus?.statuses?.every(
				status => status === TreatmentStatus.Treated,
			) &&
			treatmentStatus.endOf &&
			treatmentStatus.endOf != null &&
			queryParams.append('treatment_days_end', treatmentStatus.endOf.toString())

		if (treatmentStatus && treatmentStatus?.statuses?.length > 0) {
			queryParams.append('treatment_status', treatmentStatus.statuses.join(','))
		}
		if (inspectionStatus && inspectionStatus?.statuses?.length > 0) {
			queryParams.append(
				'examination_status',
				inspectionStatus.statuses.join(','),
			)
		}
		const isIncludesPlannedExpiredCompleted = [
			InspectionStatus.Planned,
			InspectionStatus.Expired,
			InspectionStatus.Completed,
		].some(status => inspectionStatus?.statuses.includes(status))
		if (
			inspectionStatus &&
			inspectionStatus.startOf &&
			inspectionStatus.endOf &&
			isIncludesPlannedExpiredCompleted
		) {
			queryParams.append(
				'examination_start',
				inspectionStatus.startOf.toString(),
			)
			queryParams.append('examination_end', inspectionStatus.endOf.toString())
		}
		cowAge &&
			cowAge?.startOf !== '' &&
			queryParams.append('age_start', cowAge.startOf.toString())
		cowAge &&
			cowAge?.endOf !== '' &&
			queryParams.append('age_end', cowAge.endOf.toString())
		if (fatness && fatness.startOf !== '' && fatness.endOf !== '') {
			queryParams.append('fatness_start', fatness.startOf.toString())
			queryParams.append('fatness_end', fatness.endOf.toString())
		}
		if (fatness?.statuses?.length) {
			queryParams.append('fatness_status', fatness.statuses.join(','))
		}
		if (lactation && lactation?.startOf !== '' && lactation?.endOf !== '') {
			queryParams.append('lactation_days_start', lactation.startOf.toString())
			queryParams.append('lactation_days_end', lactation.endOf.toString())
		}
		if (lactation && lactation?.statuses?.length > 0) {
			const milkingStatus = lactation.statuses.filter(
				el =>
					el === LactationStatus.Lactating ||
					el === LactationStatus.NonLactating,
			)
			const milkingTypes = lactation.statuses.filter(
				el =>
					el === LactationStatus.CommercialMilk ||
					el === LactationStatus.DefectiveMilk,
			)
			if (milkingStatus.length > 0) {
				queryParams.append('milking_status', milkingStatus.join(','))
			}
			if (milkingTypes.length > 0) {
				queryParams.append('milk_types', milkingTypes.join(','))
			}
		}
		if (groups && groups.length > 0) {
			queryParams.append('groups', groups.join(','))
		}
		if (examiners && examiners?.ids?.length > 0) {
			queryParams.append('examiners', examiners.ids.join(','))
		}
		if (diagnoses && diagnoses?.ids?.length > 0) {
			queryParams.append('diagnoses', diagnoses.ids.join(','))
		}
		if (lastTrim && lastTrim?.startOf !== '' && lastTrim?.endOf !== '') {
			queryParams.append('last_trim_start', lastTrim.startOf.toString())
			queryParams.append('last_trim_end', lastTrim.endOf.toString())
		}
		if (
			lastMilking &&
			lastMilking?.startOf !== '' &&
			lastMilking?.endOf !== ''
		) {
			queryParams.append('last_milking_start', lastMilking.startOf.toString())
			queryParams.append('last_milking_end', lastMilking.endOf.toString())
		}
		if (
			lastLamenessMeasurement &&
			lastLamenessMeasurement?.startOf !== '' &&
			lastLamenessMeasurement?.endOf !== ''
		) {
			queryParams.append(
				'limpness_change_start',
				lastLamenessMeasurement.startOf.toString(),
			)
			queryParams.append(
				'limpness_change_end',
				lastLamenessMeasurement.endOf.toString(),
			)
		}
		if (responsiblePersons && responsiblePersons?.ids?.length > 0) {
			queryParams.append('trim_responsible', responsiblePersons.ids.join(','))
		}
		if (isLateTrims) {
			queryParams.append('late_trims', isLateTrims.toString())
		}
		if (dateRange && dateRange.startDate && dateRange.endDate) {
			queryParams.append('next_trim_start', dateRange.startDate.toString())
			queryParams.append('next_trim_end', dateRange.endDate.toString())
		}
		if (page) queryParams.append('page', page.toString())
		if (pageSize) queryParams.append('page_size', pageSize.toString())
		if (identified !== undefined)
			queryParams.append('identified', String(identified))
		if (ordering === LAMENESS_EXAMINATION_ORDERING) {
			queryParams.append('ordering', 'limpness')
			queryParams.append('lameness_examination_priority', 'true')
		} else if (ordering) {
			queryParams.append('ordering', ordering)
		}
		if (tag_id_search)
			queryParams.append('tag_id_search', tag_id_search.toString())
		if (columns && columns.length > 0) {
			queryParams.append('columns', columns.join(','))
		}

		return queryParams.toString()
	}

	static getCowsList = async (
		groupId?: number,
		fatnessPoint?: number[],
		page?: number,
		pageSize?: number,
		params?: IFiltersLamenessTable,
		tag_id_search?: string,
		identified?: boolean,
		ordering?: string | null,
	) => {
		const queryParams = this.buildQueryParams(
			groupId,
			fatnessPoint,
			params?.tags,
			params?.groups?.ids,
			params?.fatness,
			params?.examiners,
			params?.diagnoses,
			params?.lameness,
			params?.treatmentStatus,
			params?.inspectionStatus,
			params?.cowAge,
			params?.lactation,
			params?.lastTrim,
			params?.lastMilking,
			params?.lastLamenessMeasurement,
			params?.responsiblePersons,
			params?.isLateTrims,
			params?.dateRange,
			page,
			pageSize,
			tag_id_search,
			identified,
			ordering,
		)

		const url = `/cows/?${queryParams}`
		return await axAPI.get<ICowsListResponse>(url)
	}
	static getExportReport = async (
		groupId?: number,
		fatnessPoint?: number[],
		page?: number,
		pageSize?: number,
		params?: IFiltersLamenessTable,
		columns?: string[],
		ordering?: string | null,
	) => {
		const queryParams = this.buildQueryParams(
			groupId,
			fatnessPoint,
			params?.tags,
			params?.groups?.ids,
			params?.fatness,
			params?.examiners,
			params?.diagnoses,
			params?.lameness,
			params?.treatmentStatus,
			params?.inspectionStatus,
			params?.cowAge,
			params?.lactation,
			params?.lastTrim,
			params?.lastMilking,
			params?.lastLamenessMeasurement,
			params?.responsiblePersons,
			params?.isLateTrims,
			params?.dateRange,
			page,
			pageSize,
			params?.tag_id_search,
			undefined,
			ordering,
			columns,
		)
		const url = `/cows/export?${queryParams}`
		return await axAPI.get<any>(url, { responseType: 'arraybuffer' })
	}

	static getExportFatnessReport = async (
		groupId?: number,
		fatnessPoint?: number[],
		page?: number,
		pageSize?: number,
		params?: IFiltersLamenessTable,
	) => {
		const queryParams = this.buildQueryParams(
			groupId,
			fatnessPoint,
			params?.tags,
			params?.groups?.ids,
			params?.fatness,
			params?.examiners,
			params?.diagnoses,
			params?.lameness,
			params?.treatmentStatus,
			params?.inspectionStatus,
			params?.cowAge,
			params?.lactation,
			params?.lastTrim,
			params?.lastMilking,
			params?.lastLamenessMeasurement,
			params?.responsiblePersons,
			params?.isLateTrims,
			params?.dateRange,
			page,
			pageSize,
			params?.tag_id_search,
		)

		const url = `/cows/fatness/export?${queryParams}`
		return await axAPI.get<any>(url, { responseType: 'arraybuffer' })
	}

	static getCowsCount = async (
		params?: IFiltersLamenessTable,
		groupId?: number,
		tag_id_search?: string,
		identified?: boolean,
	) => {
		const queryParams = this.buildQueryParams(
			groupId,
			undefined,
			params?.tags,
			params?.groups?.ids,
			params?.fatness,
			params?.examiners,
			params?.diagnoses,
			params?.lameness,
			params?.treatmentStatus,
			params?.inspectionStatus,
			params?.cowAge,
			params?.lactation,
			params?.lastTrim,
			params?.lastMilking,
			params?.lastLamenessMeasurement,
			params?.responsiblePersons,
			params?.isLateTrims,
			params?.dateRange,
			undefined,
			undefined,
			tag_id_search,
			identified,
		)

		const url = `/cows/count?${queryParams}`
		return axAPI.get<number>(url)
	}
	static getAvailableFilterValues = async () => {
		const url = '/cows/available-filter-values'
		return await axAPI.get<IAvailableFilterValuesResponse>(url)
	}
	static getCowInfoByTagId = async (tagId: number | string) => {
		return await axAPI.get<ICowsList>(`/cows/tag/${encodeURIComponent(tagId)}`)
	}
	static getCowLightInfoById = async (cowId: number) => {
		return await axAPI.get<ICowLightInfo>(`/cows/${cowId}/v2`)
	}
	static getExaminersList = async () => {
		return await axAPI.get(`/cows/examiners`)
	}
	static getDiagnosisList = async () => {
		return await axAPI.get(`/cows/sicknesses`)
	}
	static getMilkingGraphByCowId = async (
		cowId: number,
		startDate: number,
		endDate: number,
	): Promise<ICowGraphResponse> => {
		try {
			const response = await axAPI.get<ICowGraphResponse>(
				`/cows/${cowId}/milking/graph?start=${startDate}&end=${endDate}`,
			)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static getFatnessGraph = async (
		startDate: number,
		endDate: number,
		activeGroup?: number | string,
	): Promise<IFatnessGraphResponse> => {
		try {
			const queryParams = new URLSearchParams()
			if (activeGroup) queryParams.append('group_id', activeGroup.toString())
			if (startDate) queryParams.append('start', startDate.toString())
			if (endDate) queryParams.append('end', endDate.toString())

			const url = `/cows/fatness/graph?${queryParams.toString()}`
			const response = await axAPI.get<IFatnessGraphResponse>(url)
			return response.data
		} catch (err) {
			throw err
		}
	}
	static getLamenessGraph = async (
		startDate: number,
		endDate: number,
		activeGroup?: number | string,
	): Promise<ILamenessGraphResponse> => {
		try {
			const queryParams = new URLSearchParams()
			if (activeGroup) queryParams.append('group_id', activeGroup.toString())
			if (startDate) queryParams.append('start', startDate.toString())
			if (endDate) queryParams.append('end', endDate.toString())

			const url = `/cows/limpness/graph?${queryParams.toString()}`
			const response = await axAPI.get<ILamenessGraphResponse>(url)
			return response.data
		} catch (e) {
			console.error(e.message)
			throw e
		}
	}
	static getExaminationCowInfoByCowId = async (
		cow_id: number,
	): Promise<ICheckupList[]> => {
		try {
			const response = await axAPI.get<ICheckupList[]>(
				`/cows/${cow_id}/examinations`,
			)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static postCheckupCowInfoByCowId = async (
		cow_id: number,
		data: Partial<ICheckupList>,
	) => {
		return axAPI({
			method: 'POST',
			url: `/cows/${cow_id}/examinations`,
			data,
		})
	}
	static getCheckChangedWeightCattlesQty = async () => {
		return axAPI({
			method: 'GET',
			url: `/check/${EventsEnum.CATTLE_QUANTITY}`,
			validateStatus: status => status === 404 || status === 200,
		})
	}
	static postNoChangedNeededForCattlesQty = async () => {
		return axAPI({
			method: 'POST',
			url: `/check/${EventsEnum.CATTLE_QUANTITY}`,
		})
	}
	static postComfirmDigitizatedCounter = async (cameraId: number) => {
		const response = await axAPI({
			method: 'POST',
			url: `/cows/milking/${cameraId}/comfirm`,
			data: { id: cameraId },
		})
		return response
	}
	static postCowTagInfo = async obj => {
		const formData = new FormData()
		formData.append('image', obj.image)
		formData.append('timestamp', obj.timestamp)
		formData.append('angle', 'tag')
		formData.append('cow_id', obj.cow_id)

		return await axAPI({
			method: 'POST',
			url: `/cows/${obj.cow_id}/images`,
			data: formData,
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
	}
	static getTrimsByCowId = async (
		cow_id: number[],
	): Promise<ITrimsBatching[]> => {
		try {
			const queryParams = new URLSearchParams()
			cow_id && queryParams.append('cow_id', cow_id.join(','))
			const response = await axAPI.get<ITrimsBatching[]>(
				`/cows/trims/batch?${queryParams}`,
			)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static getTodayTrims = async (cow_id: number[]): Promise<ITrims[]> => {
		try {
			const queryParams = new URLSearchParams()
			cow_id && queryParams.append('cow_id', cow_id.join(','))
			const response = await axAPI.get<ITrims[]>(`/cows/trims?${queryParams}`)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static deleteTrimByTrimId = async (trim_id: number) => {
		try {
			await axAPI.delete(`/cows/trims/${trim_id}`)
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static patchTrimByTrimId = async (
		trim_id: number,
		targetDate: number,
		respId: number,
		comment?: string,
		done_dt?: number,
	) => {
		try {
			await axAPI({
				method: 'PATCH',
				url: `/cows/trims/${trim_id}`,
				data: {
					target_date: targetDate,
					responsible_id: respId,
					comment,
					done_dt,
				},
			})
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static postTrimByCowId = async (
		cow_id: number,
		targetDate: number,
		respId: number,
		comment?: string,
		done_dt?: number,
	) => {
		try {
			await axAPI({
				method: 'POST',
				url: `/cows/${cow_id}/trims`,
				data: {
					target_date: targetDate,
					responsible_id: respId,
					comment,
					done_dt,
				},
			})
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static getHoofHealthTableByCowId = async (cowId: number) => {
		try {
			const response = await axAPI.get<IHoofHealthTable[]>(
				`/cows/${cowId}/health/limbs`,
			)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static getFatnessHealthTableByCowId = async (cowId: number) => {
		try {
			const response = await axAPI.get<IFatnessHealthTable[]>(
				`/cows/${cowId}/health/fatness`,
			)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}
	static getIdentificationList = async (
		props: IIdentificationProps,
	): Promise<IIdentificationResponse> => {
		const { rec_date, camera_id, day_period, page, page_size } = props
		const queryParams = new URLSearchParams()
		if (rec_date) queryParams.append('rec_date', rec_date.toString())
		if (camera_id) queryParams.append('camera_id', camera_id.toString())
		if (day_period) queryParams.append('day_period', day_period.toString())
		if (page) queryParams.append('page', page.toString())
		if (page_size) queryParams.append('page_size', page_size.toString())

		try {
			const response = await axAPI.get(
				`/cows/identification?${queryParams.toString()}`,
			)
			return response.data
		} catch (err) {
			throw err
		}
	}
	static getDailyShifts = async (props: DailyShiftProps) => {
		const { date, cameraId } = props
		const queryParams = new URLSearchParams()
		if (date) queryParams.append('date', date.toString())
		if (cameraId) queryParams.append('camera_id', cameraId.toString())
		try {
			const response = await axAPI.get(
				`/cows/identification/shift?${queryParams.toString()}`,
			)
			return response.data
		} catch (err) {
			throw err
		}
	}
	static patchTagIdIdentification = async (props: IPatchChangeTagIdProps) => {
		const { tracking_id, body } = props
		try {
			const response = await axAPI.patch(
				`/cows/identification/${tracking_id}`,
				body,
			)
			return response.data
		} catch (err) {
			throw err
		}
	}
	static postCompleteIdentificationByShift = async (
		body: IFinishedIdentifications,
	) => {
		try {
			const response = await axAPI.post(`/cows/identification/shift`, body)
			return response.data
		} catch (err) {
			throw err
		}
	}
	static getTreatmentSchema = async (): Promise<ITreatmentSchema[]> => {
		try {
			const response = await axAPI.get('/cows/treatment-plan')
			const data = response.data
			if (Array.isArray(data)) return data
			if (Array.isArray(data?.items)) return data.items
			return []
		} catch (err) {
			throw err
		}
	}
	static getTreatmentSchemaOptions = async (): Promise<
		ITreatmentSchemaOptions[]
	> => {
		try {
			const response = await axAPI.get<ITreatmentSchemaOptionsResponse>(
				'/cows/treatment-plan/v2',
			)
			return response.data.items
		} catch (err) {
			throw err
		}
	}

	static postTreatmentSchema = async (
		data: {
			title: string
			sickness_id: number
			sicknesses_id: number[]
			duration: number
			description: string
			exam_interval_days: number
			milk_reject_days: number | null
		}[],
	) => {
		try {
			const response = await axAPI.post('/cows/treatment-plan', data)

			if (response.status !== 200 && response.status !== 201) {
				throw new Error('Ошибка при добавлении схемы')
			}

			if (response.data?.success === false || response.data?.error) {
				throw new Error(response.data?.error || 'Ошибка при добавлении схемы')
			}

			return response.data
		} catch (err) {
			throw err
		}
	}
	static putTreatmentSchema = async (
		id: number,
		data: {
			title: string
			duration: number
			description: string
		},
	) => {
		try {
			const response = await axAPI.put(`/cows/treatment-plan/${id}`, data)
			return response.data
		} catch (err) {
			throw err
		}
	}
	static patchTreatmentSchema = async (
		plan_id: number,
		data: {
			title?: string
			duration?: number
			description?: string
			sickness_id?: number
			sicknesses_id?: number[]
			exam_interval_days?: number
			milk_reject_days?: number | null
		},
	) => {
		try {
			const response = await axAPI.patch(
				`/cows/treatment-plan/${plan_id}`,
				data,
			)
			return response.data
		} catch (err) {
			throw err
		}
	}
	static deleteTreatmentSchema = async (plan_id: number) => {
		try {
			await axAPI.delete(`/cows/treatment-plan/${plan_id}`)
		} catch (err) {
			throw err
		}
	}
	static getAvailableCowTags = async (): Promise<IAvailableCowTags> => {
		const response = await axAPI.get('/cows/available-tags')
		return response.data
	}

	static getMilkYieldChartInfo = async (
		props: IMilkYieldChartRequest,
	): Promise<IMilkYieldChartResponse> => {
		const { start, end, barn_id, group_id } = props
		const queryParams = new URLSearchParams()
		if (start) queryParams.append('start', start.toString())
		if (end) queryParams.append('end', end.toString())
		if (barn_id) queryParams.append('barn_id', barn_id.toString())
		if (group_id) queryParams.append('group_id', group_id.toString())

		const response = await axAPI.get(
			`/cows/milkings/graph?${queryParams.toString()}`,
		)
		return response.data
	}
	static getMilkYieldTableData = async (params: IMilkingTableRequest) => {
		const {
			date,
			group_id,
			barn_id,
			period,
			page,
			page_size,
			tag_id,
			ordering,
			tag_id_search,
		} = params

		if (!date) {
			throw new Error('Date is required for milk yield table request')
		}

		const queryParams = new URLSearchParams()
		if (group_id) queryParams.append('group_id', group_id.toString())
		if (barn_id) queryParams.append('barn_id', barn_id.toString())
		if (period) queryParams.append('period', period)
		if (ordering) queryParams.append('ordering', ordering.toString())
		if (page) queryParams.append('page', page.toString())
		if (page_size) queryParams.append('page_size', page_size.toString())
		if (tag_id?.length > 0) queryParams.append('tag_id', tag_id.join(','))
		if (tag_id_search)
			queryParams.append('tag_id_search', tag_id_search.toString())

		const response = await axAPI.get<IMilkingTableResponse>(
			`/cows/milkings/${date}?${queryParams.toString()}`,
		)

		return response.data
	}

	static getMilkYieldTableExport = async (
		params: Omit<IMilkingTableRequest, 'page' | 'page_size'> & {
			period?: IMilkingTableRequest['period']
			tag_id?: IMilkingTableRequest['tag_id']
		},
	) => {
		const { date, group_id, barn_id, period, tag_id, ordering, tag_id_search } =
			params

		if (!date) {
			throw new Error('Date is required for milk yield export request')
		}

		const queryParams = new URLSearchParams()
		if (group_id) queryParams.append('group_id', group_id.toString())
		if (barn_id) queryParams.append('barn_id', barn_id.toString())
		if (period) queryParams.append('period', period)
		if (ordering) queryParams.append('ordering', ordering.toString())
		if (tag_id?.length > 0) queryParams.append('tag_id', tag_id.join(','))
		if (tag_id_search)
			queryParams.append('tag_id_search', tag_id_search.toString())

		return await axAPI.get(
			`/cows/milkings/${date}/export?${queryParams.toString()}`,
			{
				responseType: 'arraybuffer',
			},
		)
	}
}
